/* eslint-disable @typescript-eslint/no-empty-function */
// @file Vuex Store module for Realtime functionality
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import type { Comment, JsonAPIResponse, Wish } from '@padlet/arvo'
import { getSocket } from 'bits/brahms'
import environment from 'bits/environment'
import { captureException, captureMessage } from 'bits/error_tracker'
import { isAppUsing } from 'bits/flip'
import window from 'bits/global'
import { __ } from 'bits/intl'
import { navigateTo } from 'bits/location'
import ping from 'bits/ping'
import { NATIVE_HOST } from 'bits/url'
import { debounce } from 'lodash-es'
import * as Phoenix from 'phoenix'
import { useSurfacePostsStore } from 'pinia/surface_posts'
import { useReactionsStore } from 'pinia/surface_reactions'
import { useSurfaceSettingsStore } from 'pinia/surface_settings'
import type { AccumulatedReactions } from 'types'
import type { Module } from 'vuex'
import type { RootState } from 'vuexstore/surface/types'

interface RealtimeState {
  wallChannelLastConnected: number | null
  wallChannelLastDisconnected: number | null
  pingContributingStatusToken: string | null
  brahmsToken: string | null
  deviceId: string | null
  brahmsSocket: Phoenix.Socket | null
  wallBrahmsChannel: Phoenix.Channel | null
  deviceBrahmsChannel: Phoenix.Channel | null
  dispatchQueue: any[]
  isClearingQueue: boolean
  notificationUid: string | null
  userId: number | null
  isPresencePresent: boolean
  shouldUseLongPoll: boolean
}

const defaultState = (): RealtimeState => ({
  wallChannelLastConnected: null,
  wallChannelLastDisconnected: null,
  pingContributingStatusToken: null,
  brahmsToken: null,
  deviceId: null,
  brahmsSocket: null,
  wallBrahmsChannel: null,
  deviceBrahmsChannel: null,
  dispatchQueue: [],
  isClearingQueue: false,
  notificationUid: null,
  userId: null,
  isPresencePresent: false,
  shouldUseLongPoll: false,
})

function userNeedsFocus(rootGetters): boolean {
  const isBusyReacting = !!rootGetters['reaction/postIdBeingReactedTo']
  const isBusyCommenting =
    rootGetters['comment/isCommenting'] &&
    rootGetters['comment/inlineCommentInputLastTypedInMs'] &&
    Date.now() - rootGetters['comment/inlineCommentInputLastTypedInMs'] < 30000
  return isBusyReacting || isBusyCommenting
}

const RECONNECT_DELAY = [50, 75, 100, 200, 500, 1000, 2000, 5000]
const MAX_ALIVE_BEACON_WAITTIME_MS = 10000

let refreshWallSectionsReceivingTimeout: ReturnType<typeof setTimeout> | null = null

const RealtimeModule: Module<RealtimeState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    areUpdatesPending(state): boolean {
      return state.dispatchQueue.length > 0
    },
    isConnected(state): boolean {
      const connectedTime = state.wallChannelLastConnected
      const disconnectedTime = state.wallChannelLastDisconnected
      if (!connectedTime) return false
      if (!disconnectedTime) return true
      return connectedTime > disconnectedTime
    },
    isRealtimeDownSinceLastSync(state, getters): (lastSyncTime: number) => boolean {
      return (lastSyncTime: number): boolean => {
        if (!getters.isConnected) return true
        const connectedTime = state.wallChannelLastConnected as number
        return lastSyncTime <= connectedTime
      }
    },
  },
  mutations: {
    INITIALIZE_STATE(state, data): void {
      Object.assign(state, data)
    },
    SET_BRAHMS_TOKEN(state, brahmsToken): void {
      state.brahmsToken = brahmsToken
    },
    SET_BRAHMS_SOCKET(state, brahmsSocket): void {
      state.brahmsSocket = brahmsSocket
    },
    SET_WALL_BRAHMS_CHANNEL(state, wallBrahmsChannel): void {
      state.wallBrahmsChannel = wallBrahmsChannel
    },
    SET_DEVICE_BRAHMS_CHANNEL(state, deviceBrahmsChannel): void {
      state.deviceBrahmsChannel = deviceBrahmsChannel
    },
    QUEUE_UPDATE_ACTION(state, action): void {
      state.dispatchQueue.push(action)
    },
    START_CLEARING_DISPATCH_QUEUE(state): void {
      state.isClearingQueue = true
    },
    CLEAR_DISPATCH_QUEUE(state): void {
      state.dispatchQueue = []
    },
    END_CLEARING_DISPATCH_QUEUE(state): void {
      state.isClearingQueue = false
    },
    SET_NOTIFICATION_UID(state, uid): void {
      state.notificationUid = uid
    },
    CLEAR_NOTIFICATION_UID(state): void {
      state.notificationUid = null
    },
    WALL_CHANNEL_CONNECTED(state): void {
      state.wallChannelLastConnected = Date.now()
    },
    WALL_CHANNEL_DISCONNECTED(state): void {
      state.wallChannelLastDisconnected = Date.now()
    },
    SET_IS_PRESENCE_PRESENT(state, isPresencePresent): void {
      state.isPresencePresent = isPresencePresent
    },
    UPDATE_SHOULD_USE_LONG_POLL(state, shouldUseLongPoll): void {
      state.shouldUseLongPoll = shouldUseLongPoll
    },
  },
  actions: {
    initializeState({ commit, dispatch }, data): void {
      commit('INITIALIZE_STATE', data)
      void dispatch('checkIfBrahmsMessagesAreReceived')
      void dispatch('checkIfWebsocketIsEnabled')
    },

    checkIfBrahmsMessagesAreReceived({ commit, dispatch }): void {
      refreshWallSectionsReceivingTimeout = setTimeout(() => {
        captureMessage('refresh_wall_sections is not received via WebSocket')
        if (isAppUsing('brahmsCheck')) {
          refreshWallSectionsReceivingTimeout = setTimeout(() => {
            captureMessage('refresh_wall_sections is not received via LongPoll')
          }, MAX_ALIVE_BEACON_WAITTIME_MS)
        }
        void dispatch('fallBackToLongPolling')
      }, 2000)
    },

    checkIfWebsocketIsEnabled({ dispatch, state }): void {
      if (environment !== 'production') return
      const pingPongTimeout = setTimeout(() => {
        captureMessage('Websocket is not enabled')
        void dispatch('fallBackToLongPolling')
      }, 2000)

      const socket = new WebSocket(`wss://${NATIVE_HOST}/diagnostics/websocket`)
      socket.addEventListener('message', (event) => {
        if (event.data === 'pong') {
          clearTimeout(pingPongTimeout)
          socket.close()
        }
      })
      socket.addEventListener('open', () => {
        socket.send('ping')
      })
    },
    fallBackToLongPolling({ commit, state }): void {
      commit('UPDATE_SHOULD_USE_LONG_POLL', true)
      state.brahmsSocket?.replaceTransport(Phoenix.LongPoll)
      state.brahmsSocket?.connect()
    },

    connect({ dispatch, state }): void {
      dispatch('connectToBrahms')
      dispatch('subscribeToStoreChanges')
    },

    connectToBrahms({ dispatch }): void {
      const brahmsConnection: Phoenix.Socket | undefined = window.ww?.brahms?.socket
      if (brahmsConnection === undefined) {
        if (isAppUsing('realtimeFetching')) captureMessage('Brahms failed to initialize sooner')
        void dispatch('createNewBrahmsConnection')
        return
      }
      void dispatch('updateCurrentBrahmsConnection', brahmsConnection)
    },

    disconnectBrahms({ commit, state }): void {
      state.brahmsSocket != null && state.brahmsSocket.disconnect()
      commit('SET_BRAHMS_SOCKET', null)
    },

    absent({ commit, state }): void {
      if (state.isPresencePresent) {
        commit('SET_IS_PRESENCE_PRESENT', false)
        state.wallBrahmsChannel != null && state.wallBrahmsChannel.push('absent', {})
      }
    },

    present({ commit, state }): void {
      commit('SET_IS_PRESENCE_PRESENT', true)
      state.wallBrahmsChannel != null && state.wallBrahmsChannel.push('present', {})
    },

    clearMessageQueue({ dispatch }): void {
      if (window.ww.brahms == null) return
      const messageQueue = window.ww.brahms?.messageQueue
      if (messageQueue == null) return
      void Promise.all(messageQueue.map(async (message) => await dispatch('handleMessage', message)))
      window.ww.brahms.messageQueue = []
    },

    createNewBrahmsConnection({ state, commit, dispatch }): void {
      if (!state.brahmsToken) return
      let connectionRetries = 0
      const brahmsConnection = getSocket(
        { accessToken: state.brahmsToken, deviceId: state.deviceId, shouldUseLongPoll: state.shouldUseLongPoll },
        (tries) => {
          connectionRetries++
          return RECONNECT_DELAY[tries - 1] || 10000
        },
      )
      brahmsConnection?.onError((_, transport, establishedConnections) => {
        if (transport === WebSocket && establishedConnections === 0 && connectionRetries > 1) {
          brahmsConnection?.replaceTransport(Phoenix.LongPoll)
          brahmsConnection?.connect()
        }
      })
      brahmsConnection?.connect()
      commit('SET_BRAHMS_SOCKET', brahmsConnection)

      void dispatch('createWallBrahmsChannel')
      void dispatch('createDeviceBrahmsChannel')
      void dispatch('createUserBrahmsChannel')
    },

    async updateCurrentBrahmsConnection({ commit, dispatch }, brahmsConnection: Phoenix.Socket): Promise<void> {
      commit('SET_BRAHMS_SOCKET', brahmsConnection)

      const wallBrahmsChannel = window.ww.brahms?.channels.wall
      await dispatch('addWallBrahmsChannelHandlers', wallBrahmsChannel)
      wallBrahmsChannel?.off('new_msg', window.ww.brahms?.msgRefs.wall)
      commit('WALL_CHANNEL_CONNECTED')
      commit('SET_WALL_BRAHMS_CHANNEL', wallBrahmsChannel)

      const deviceBrahmsChannel = window.ww.brahms?.channels.device
      await dispatch('addDeviceBrahmsChannelHandlers', deviceBrahmsChannel)
      deviceBrahmsChannel?.off('new_msg', window.ww.brahms?.msgRefs.device)
      commit('SET_DEVICE_BRAHMS_CHANNEL', deviceBrahmsChannel)

      void dispatch('clearMessageQueue')
    },

    createWallBrahmsChannel({ state, dispatch, commit, rootGetters }): void {
      const wallId = rootGetters.wallId
      if (state.brahmsSocket == null) return
      try {
        const wallBrahmsChannel: Phoenix.Channel = state.brahmsSocket.channel(`wall:${wallId}`)
        void dispatch('addWallBrahmsChannelHandlers', wallBrahmsChannel)
        wallBrahmsChannel.join().receive('ok', async () => {
          commit('WALL_CHANNEL_CONNECTED')
        })
        commit('SET_WALL_BRAHMS_CHANNEL', wallBrahmsChannel)
      } catch (e) {
        captureException(e)
      }
    },

    addWallBrahmsChannelHandlers({ commit, dispatch }, wallBrahmsChannel: Phoenix.Channel): void {
      wallBrahmsChannel?.on('new_msg', (data) => {
        void dispatch('handleMessage', data)
      })
      wallBrahmsChannel?.onError(() => {
        commit('WALL_CHANNEL_DISCONNECTED')
      })
      wallBrahmsChannel?.onClose(() => {
        commit('WALL_CHANNEL_DISCONNECTED')
      })
    },

    createDeviceBrahmsChannel({ state, commit, dispatch }): void {
      if (state.brahmsSocket == null) return
      try {
        const deviceBrahmsChannel: Phoenix.Channel = state.brahmsSocket.channel(`device:${state.deviceId}`)
        void dispatch('addDeviceBrahmsChannelHandlers', deviceBrahmsChannel)
        deviceBrahmsChannel.join()
        commit('SET_DEVICE_BRAHMS_CHANNEL', deviceBrahmsChannel)
      } catch (e) {
        captureException(e)
      }
    },

    addDeviceBrahmsChannelHandlers({ dispatch }, deviceBrahmsChannel: Phoenix.Channel): void {
      deviceBrahmsChannel.on('new_msg', (msg) => {
        void dispatch('handleMessage', msg)
      })
    },

    createUserBrahmsChannel({ state }): void {
      if (state.brahmsSocket != null) {
        const userId = state.userId
        const userChannel: Phoenix.Channel = state.brahmsSocket.channel(`user:${userId}`)
        userChannel.join()
      }
    },

    subscribeToStoreChanges({ dispatch, getters, rootGetters }): void {
      const flushUpdates = (): void => {
        if (getters.areUpdatesPending && !userNeedsFocus(rootGetters)) {
          dispatch('dispatchAllQueuedActions')
        }
      }
      const debouncedFlushUpdates = debounce(flushUpdates, 200, { leading: true })
      this.subscribe((): void => debouncedFlushUpdates())
    },

    pingContributingStatus({ state }): void {
      state.wallBrahmsChannel?.push('ping_contributing_status', {})
    },

    updateContributingStatus({ state }, { status }): void {
      state.wallBrahmsChannel?.push('update_contributing_status', { status })
    },

    async fetchAccumulatedReactions({ state }): Promise<AccumulatedReactions> {
      return await new Promise((resolve, reject) => {
        if (state.wallBrahmsChannel == null) return reject(Error('Undefined wall channel'))
        state.wallBrahmsChannel
          .push('fetch_accumulated_reactions', {}, 5000)
          .receive('ok', (data) => {
            resolve(data)
          })
          .receive('error', (error) => {
            reject(error)
          })
          .receive('timeout', () => reject(Error('Timeout')))
      })
    },

    async fetchComments({ state }, query: { pageStart?: String }): Promise<JsonAPIResponse<Comment>> {
      return await new Promise((resolve, reject) => {
        if (state.wallBrahmsChannel == null) return reject(Error('Undefined wall channel'))
        state.wallBrahmsChannel
          .push('fetch_comments', query, 5000)
          .receive('ok', (data) => {
            resolve(data)
          })
          .receive('error', (error) => {
            reject(error)
          })
          .receive('timeout', () => reject(Error('Timeout')))
      })
    },

    async fetchWishes({ state }, query: { pageStart?: String }): Promise<JsonAPIResponse<Wish>> {
      return await new Promise((resolve, reject) => {
        if (state.wallBrahmsChannel == null) return reject(Error('Undefined wall channel'))
        state.wallBrahmsChannel
          .push('fetch_wishes', query, 5000)
          .receive('ok', (data) => {
            resolve(data)
          })
          .receive('error', (error) => {
            reject(error)
          })
          .receive('timeout', () => reject(Error('Timeout')))
      })
    },

    handleMessage({ dispatch, rootState, rootGetters, state }, messageObj): void {
      if (!messageObj || !messageObj.event || messageObj.uid === rootState.uid) return
      const payload = JSON.parse(messageObj.message)
      // Signal that realtime is working
      ping('realtime', { rate: 20000 })
      const dispatchNow = async (type: string): Promise<any> => await dispatch(type, payload, { root: true })
      const queue = async (type: string): Promise<any> => await dispatch('queueUpdateAction', { type, payload })
      // We add a delay if the post has an attachment, so that we have sufficient
      // lead time for request coalescing to come into play.
      // See original commit: https://github.com/padlet/mozart/commit/9f9e0d60f3934c0e1c6ef411af97e1e24f429151
      const delay = (type: string): void => {
        const upTo1andHalfSecDelay = (Math.random() + 0.5) * 1000
        payload.attachment
          ? setTimeout((): void => {
              queue(type)
            }, upTo1andHalfSecDelay)
          : queue(type)
      }

      // Right now we are calling 'remote' actions in the modules themselves.
      // Eventually, the modules will have no concept of remote updates.
      // They'll only have simple CRUD operations that'll be called by this guy.

      const handlers = {
        edit_wall(): void {
          dispatchNow('updateWall')
          void useSurfaceSettingsStore()?.updatePreviewAttributes(payload)
          const newDefaultSectionId = payload.wish_arrangement?.default_section_id
          if (newDefaultSectionId != null) {
            void dispatch('section/changeDefaultSection', newDefaultSectionId, { root: true })
          }
        },
        user_edited_wall(): void {
          const { user } = payload
          void useSurfaceSettingsStore()?.notifyWallSettingsUpdate(rootGetters.user.id, user)
        },
        delete_wall(): void {
          if (rootGetters.isLibraryWall as boolean) {
            navigateTo(rootGetters.libraryDashboardUrl)
          } else {
            navigateTo('/')
          }
        },
        add_section(): void {
          dispatchNow('section/newSectionRemote')
        },
        edit_section(): void {
          dispatchNow('section/editSectionRemote')
        },
        delete_section(): void {
          dispatchNow('section/deleteSectionRemote')
        },
        add_wish(): void {
          delay('post/newPostRemote')
        },
        new_poll_vote(): void {
          delay('post/newPollVoteRemote')
        },
        edit_poll(): void {
          delay('post/editPollRemote')
        },
        edit_wish(): void {
          delay('post/editPostRemote')
        },
        edit_wish_order(): void {
          delay('post/editPostOrderRemote')
        },
        batch_edit_wish(): void {
          delay('post/batchEditPostRemote')
        },
        delete_wish(): void {
          dispatchNow('deletePostRemote')
        },
        wish_hidden_to_wait_for_auto_moderation(): void {
          dispatchNow('post/removePostToWaitForAutoModeration')
        },
        // we don't use delay() for drafts because they only exist for their authors
        // we don't expect that changing an attachment would result in many other connected clients requesting for that new attachment in real time
        add_wish_draft(): void {
          useSurfaceDraftsStore().insertDraftRemote(payload)
        },
        edit_wish_draft(): void {
          useSurfaceDraftsStore().updateDraftRemote(payload)
        },
        delete_wish_draft(): void {
          useSurfaceDraftsStore().removeDraftRemote(payload)
        },
        refresh_wall_sections(): void {
          if (refreshWallSectionsReceivingTimeout != null) {
            clearTimeout(refreshWallSectionsReceivingTimeout)
            refreshWallSectionsReceivingTimeout = null
          }
          if (isAppUsing('brahmsCheck')) {
            if (state.shouldUseLongPoll) {
              captureMessage('refresh_wall_sections is received via LongPoll')
            } else {
              captureMessage('refresh_wall_sections is received via WebSocket')
            }
          }
          if (isAppUsing('realtimeFetching')) {
            if (rootGetters.canUseSections) dispatchNow('section/refreshSections')
          }
        },
        refresh_wishes(): void {
          if (isAppUsing('realtimeFetching')) {
            dispatchNow('post/refreshPosts')
          }
        },
        refresh_comments(): void {
          if (isAppUsing('realtimeFetching')) {
            dispatchNow('comment/refreshComments')
          }
        },
        refresh_accumulated_reactions(): void {
          if (isAppUsing('realtimeFetching')) {
            void dispatchNow('reaction/refreshAccumulatedReactions')
          }
        },
        refresh_padlet_starting_state(): void {
          if (isAppUsing('realtimeFetching')) {
            dispatchNow('refreshPadletStartingState')
          }
        },
        add_comment(): void {
          queue('comment/newCommentRemote')
        },
        edit_comment(): void {
          queue('comment/editCommentRemote')
        },
        delete_comment(): void {
          queue('comment/deleteCommentRemote')
        },
        add_reaction(): void {
          dispatchNow('reaction/newReactionRemote')
        },
        edit_reaction(): void {
          dispatchNow('reaction/editReactionRemote')
        },
        delete_reaction(): void {
          dispatchNow('reaction/deleteReactionRemote')
        },
        update_accumulated_reactions(): void {
          useReactionsStore().updateAccumulatedReactions(payload)
        },
        update_user_reaction(): void {
          useReactionsStore().updateUserReaction(payload)
        },
        add_connection(): void {
          dispatchNow('postConnection/newConnectionRemote')
        },
        delete_connection(): void {
          dispatchNow('postConnection/deleteConnectionRemote')
        },
        edit_table_layout(): void {
          queue('table/editLayoutRemote')
        },
        edit_table_row(): void {
          queue('table/editRowRemote')
        },
        edit_table_col(): void {
          queue('table/editColRemote')
        },
        contributing_status(): void {
          dispatchNow('contributingStatus/updateRemote')
        },
        ping_contributing_status(): void {
          dispatchNow('contributingStatus/pushSetIfActive')
        },
        login(): void {
          window.location.reload()
        },
        logout(): void {
          window.location.reload()
        },
        collaborator_left(): void {
          dispatchNow('wallAccessSettings/removeCollaboratorRemotely')
        },
        wall_follow(): void {
          dispatchNow('remoteUpdateWallFollows')
        },
        wall_follows_count(): void {
          dispatchNow('remoteUpdateWallFollowsCount')
        },
        self_harm_post_detected(): void {
          useSurfacePostsStore().showSelfHarmPostAlert(payload)
        },
      }

      const handler = handlers[messageObj.event]
      handler && handler()
    },

    queueUpdateAction({ dispatch, commit, getters, rootGetters }, { type, payload }): void {
      if (userNeedsFocus(rootGetters)) {
        if (!getters.areUpdatesPending) dispatch('setUpdatesPendingNotification')
        commit('QUEUE_UPDATE_ACTION', { type, payload })
      } else {
        dispatch(type, payload, { root: true })
      }
    },

    setUpdatesPendingNotification({ dispatch, commit }): void {
      dispatch(
        'snackbars/setSnackbar',
        {
          message: __('Padlet updated elsewhere.'),
          actionText: __('Update'),
          actionName: 'realtime/dispatchAllQueuedActions',
          persist: true,
        },
        { root: true },
      ).then((uid): void => commit('SET_NOTIFICATION_UID', uid))
    },

    clearUpdatesPendingNotification({ dispatch, commit, state }): void {
      dispatch('snackbars/clearSnackbar', state.notificationUid, { root: true })
      commit('CLEAR_NOTIFICATION_UID')
    },

    dispatchAllQueuedActions({ commit, dispatch, state }): void {
      if (state.isClearingQueue) return
      commit('START_CLEARING_DISPATCH_QUEUE')
      state.dispatchQueue.forEach(
        async ({ type, payload }): Promise<any> => await dispatch(type, payload, { root: true }),
      )
      dispatch('clearDispatchQueue')
      commit('END_CLEARING_DISPATCH_QUEUE')
    },

    clearDispatchQueue({ dispatch, commit }): void {
      dispatch('clearUpdatesPendingNotification')
      commit('CLEAR_DISPATCH_QUEUE')
    },
  },
}

export default RealtimeModule
export type { RealtimeState }
